/**
 * Bero platform console — Team. GET /api/platform/members lists platform_members
 * rows plus any pending staff invites (SUPER_ADMIN only). Role is changed via
 * Chip tap (optimistic PATCH); active/inactive via a plain Activate/Deactivate
 * Button (soft-delete only — PlatformMember rows are never removed).
 *
 * Adding someone new SENDS AN INVITE: they set their own password via
 * /claim-owner and land with exactly the role picked here. Onboarding staff used
 * to mean running bootstrap_platform_admin.py over SSH, which also made everyone
 * SUPER_ADMIN (owner, 2026-07-22: "why should a sales person have super admin
 * privileges?"). If the email already has a platform account, the same button
 * just grants it the role instead.
 */
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { Icon } from '@/features/account-admin/icons';
import { useFetch } from '@/features/community/lib';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Chip, EmptyState, Field, Hairline, Input, Screen, SectionLabel, Skeleton } from '@/ui';

type Role = 'SUPER_ADMIN' | 'SALES' | 'SUPPORT' | 'MARKETING';
type Member = { id: number; userId: number; role: Role; isActive: boolean; fullName: string; email: string };
type PendingInvite = { id: number; email: string; fullName: string; platformRole: Role | null; expiresAt: string | null };
type TeamData = { members: Member[]; pending: PendingInvite[] };

const ROLES: Role[] = ['SUPER_ADMIN', 'SALES', 'SUPPORT', 'MARKETING'];
const ROLE_LABEL: Record<Role, DictKey> = {
  SUPER_ADMIN: 'plat_role_super_admin', SALES: 'plat_role_sales',
  SUPPORT: 'plat_role_support', MARKETING: 'plat_role_marketing',
};

async function fetchTeam(): Promise<TeamData> {
  const body = await api.get<{ items: Member[]; pendingInvites: PendingInvite[] }>('/api/platform/members');
  return {
    members: Array.isArray(body.items) ? body.items : [],
    pending: Array.isArray(body.pendingInvites) ? body.pendingInvites : [],
  };
}

function MemberRow({
  member, onChangeRole, onToggleActive,
}: {
  member: Member;
  onChangeRole: (id: number, role: Role) => Promise<void>;
  onToggleActive: (id: number, isActive: boolean) => Promise<void>;
}) {
  const { colors } = useTheme();
  const { t } = useT();
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(false);

  const changeRole = async (role: Role) => {
    if (role === member.role || busy) return;
    setBusy(true);
    setErr(false);
    try {
      await onChangeRole(member.id, role);
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    if (busy) return;
    setBusy(true);
    setErr(false);
    try {
      await onToggleActive(member.id, !member.isActive);
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={{ paddingVertical: space.s3, paddingHorizontal: space.s2, gap: space.s2 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <AppText variant="bodyMd" weight="500" style={!member.isActive ? { opacity: 0.5 } : undefined}>
            {member.fullName}
          </AppText>
          <AppText variant="bodySm" tone="tertiary">{member.email}</AppText>
        </View>
        <Button
          title={member.isActive ? t('plat_members_deactivate') : t('plat_members_activate')}
          kind={member.isActive ? 'destructive' : 'secondary'}
          loading={busy}
          onPress={() => void toggleActive()}
        />
      </View>
      <View style={{ flexDirection: 'row', gap: space.s2, flexWrap: 'wrap' }}>
        {ROLES.map((r) => (
          <Chip key={r} label={t(ROLE_LABEL[r])} active={member.role === r} onPress={() => void changeRole(r)} />
        ))}
      </View>
      {err ? <AppText variant="bodySm" style={{ color: colors.danger500 }}>{t('plat_members_err')}</AppText> : null}
    </View>
  );
}

/** Someone invited but not yet set up — visible so a pending teammate isn't
 * invisible until they click the link, with resend/cancel to hand. */
function PendingRow({ invite, onDone }: { invite: PendingInvite; onDone: () => void }) {
  const { colors } = useTheme();
  const { t } = useT();
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  const run = async (fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      await fn();
      onDone();
    } catch (e) {
      setErr((e as { error?: string })?.error ?? t('plat_members_err'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={{ paddingVertical: space.s3, paddingHorizontal: space.s2, gap: space.s2 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <AppText variant="bodyMd" weight="500">{invite.fullName}</AppText>
          <AppText variant="bodySm" tone="tertiary">{invite.email}</AppText>
          <AppText variant="bodySm" tone="tertiary">{t('plat_members_pending')}</AppText>
        </View>
        {invite.platformRole ? (
          <Chip label={t(ROLE_LABEL[invite.platformRole])} active onPress={() => {}} />
        ) : null}
      </View>
      <View style={{ flexDirection: 'row', gap: space.s2, flexWrap: 'wrap' }}>
        <Button
          title={t('plat_members_resend')}
          kind="secondary"
          loading={busy}
          onPress={() => void run(() => api.post('/api/platform/members/resend-invite', { email: invite.email }))}
        />
        <Button
          title={t('plat_members_revoke')}
          kind="destructive"
          onPress={() => void run(() => api.delete(`/api/platform/members/invite?email=${encodeURIComponent(invite.email)}`))}
        />
      </View>
      {err ? <AppText variant="bodySm" style={{ color: colors.danger500 }}>{err}</AppText> : null}
    </View>
  );
}

export default function PlatformMembersScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { data, loading, error, refetch, mutate } = useFetch(fetchTeam, []);
  const rows = data?.members ?? [];
  const pending = data?.pending ?? [];

  const [email, setEmail] = React.useState('');
  const [fullName, setFullName] = React.useState('');
  const [role, setRole] = React.useState<Role>('SUPPORT');
  const [addBusy, setAddBusy] = React.useState(false);
  const [addErr, setAddErr] = React.useState('');
  const [notice, setNotice] = React.useState('');

  const changeRole = async (id: number, r: Role) => {
    const prev = data;
    mutate((p) => (p ? { ...p, members: p.members.map((m) => (m.id === id ? { ...m, role: r } : m)) } : p));
    try {
      await api.patch(`/api/platform/members/${id}`, { role: r });
    } catch (e) {
      mutate(() => prev ?? null);
      throw e;
    }
  };

  const toggleActive = async (id: number, isActive: boolean) => {
    const prev = data;
    mutate((p) => (p ? { ...p, members: p.members.map((m) => (m.id === id ? { ...m, isActive } : m)) } : p));
    try {
      await api.patch(`/api/platform/members/${id}`, { isActive });
    } catch (e) {
      mutate(() => prev ?? null);
      throw e;
    }
  };

  const addMember = async () => {
    if (addBusy || !email.trim()) return;
    setAddBusy(true);
    setAddErr('');
    setNotice('');
    try {
      const res = await api.post<{ invited?: boolean; emailSent?: boolean }>(
        '/api/platform/members', { email: email.trim(), fullName: fullName.trim(), role },
      );
      if (res?.invited) {
        setNotice(res.emailSent === false
          ? t('plat_members_invite_failed')
          : t('plat_members_invite_sent', { email: email.trim() }));
      }
      setEmail('');
      setFullName('');
      setRole('SUPPORT');
      void refetch();
    } catch (e) {
      setAddErr((e as { error?: string })?.error ?? t('plat_members_err'));
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <Screen padded fullWidth style={{ paddingTop: space.s5 }}>

      <View style={{ maxWidth: 480, gap: space.s3, marginBottom: space.s6 }}>
        <SectionLabel>{t('plat_members_add')}</SectionLabel>
        <Field label={t('plat_members_email')} error={addErr || undefined}>
          <Input
            value={email}
            onChangeText={setEmail}
            autoCapitalize="none"
            keyboardType="email-address"
            placeholder="them@usebero.com"
            hasError={!!addErr}
            editable={!addBusy}
          />
        </Field>
        <Field label={t('plat_members_name')} help={t('plat_members_name_help')}>
          <Input value={fullName} onChangeText={setFullName} editable={!addBusy}
            returnKeyType="go" onSubmitEditing={() => void addMember()} />
        </Field>
        <Field label={t('plat_members_role')} help={t('plat_members_role_help')}>
          <View style={{ flexDirection: 'row', gap: space.s2, flexWrap: 'wrap' }}>
            {ROLES.map((r) => (
              <Chip key={r} label={t(ROLE_LABEL[r])} active={role === r} onPress={() => setRole(r)} />
            ))}
          </View>
        </Field>
        <Button
          title={t('plat_members_invite')}
          loading={addBusy}
          onPress={() => void addMember()}
          style={{ alignSelf: 'flex-start' }}
        />
        {notice ? (
          <AppText variant="bodySm" style={{ color: colors.success500 }}>{notice}</AppText>
        ) : null}
      </View>

      {loading ? (
        <View style={{ gap: space.s2 }}>
          {[0, 1, 2].map((i) => <Skeleton key={i} h={72} />)}
        </View>
      ) : error ? (
        <EmptyState
          icon={<Icon name="alert-triangle" size={40} color={colors.textTertiary} />}
          title={t('plat_members_err')}
          cta={t('retry')}
          onCta={() => void refetch()}
        />
      ) : rows.length === 0 && pending.length === 0 ? (
        <EmptyState
          icon={<Icon name="users" size={40} color={colors.textTertiary} />}
          title={t('plat_members_empty')}
        />
      ) : (
        <View>
          {rows.map((m, i) => (
            <View key={m.id}>
              <MemberRow member={m} onChangeRole={changeRole} onToggleActive={toggleActive} />
              {i < rows.length - 1 || pending.length > 0 ? <Hairline /> : null}
            </View>
          ))}
          {pending.map((inv, i) => (
            <View key={`inv-${inv.id}`}>
              <PendingRow invite={inv} onDone={() => void refetch()} />
              {i < pending.length - 1 ? <Hairline /> : null}
            </View>
          ))}
        </View>
      )}
    </Screen>
  );
}
